import React, { useState } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { googleFormsService } from "@/services/integrations/googleFormsService";
import { ExternalLink, Send } from "lucide-react";

interface GoogleSurveyIntegrationProps {
  giftId?: string;
  recipientEmail?: string;
  onSurveyCreated?: (surveyUrl: string) => void;
}

export function GoogleSurveyIntegration({ 
  giftId, 
  recipientEmail, 
  onSurveyCreated 
}: GoogleSurveyIntegrationProps) {
  const [surveyTitle, setSurveyTitle] = useState('Gift Feedback Survey');
  const [surveyDescription, setSurveyDescription] = useState('We would love to hear your feedback about the gift you received.');
  const [questions, setQuestions] = useState([
    'How satisfied were you with the gift?',
    'Would you recommend this type of gift to others?',
    'Any additional comments or suggestions?'
  ]);
  const [isCreating, setIsCreating] = useState(false);
  const [surveyUrl, setSurveyUrl] = useState('');
  
  const { toast } = useToast();

  const createGoogleForm = async () => {
    setIsCreating(true);
    
    try {
      if (!giftId || !recipientEmail) {
        throw new Error('Gift ID and recipient email are required');
      }

      // Get organization ID
      const { data: gift } = await supabase
        .from('gifts')
        .select('organization_id, name')
        .eq('id', giftId)
        .single();

      if (!gift) throw new Error('Gift not found');

      // Initialize Google Forms service
      await googleFormsService.initialize(gift.organization_id);

      // Create the survey
      const result = await googleFormsService.createGiftFeedbackSurvey(
        giftId,
        recipientEmail,
        gift.name || 'Your Gift'
      );

      setSurveyUrl(result.form_url);
      
      toast({
        title: "Survey Created",
        description: "Your Google Forms survey has been created successfully.",
      });

      if (onSurveyCreated) {
        onSurveyCreated(result.form_url);
      }

    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to create survey. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  const sendSurveyEmail = async () => {
    if (!surveyUrl || !recipientEmail) {
      toast({
        title: "Missing Information",
        description: "Survey URL or recipient email is missing.",
        variant: "destructive",
      });
      return;
    }

    try {
      // In a real implementation, you would integrate with an email service
      // For now, we'll just log the email content
      const emailContent = {
        to: recipientEmail,
        subject: 'Feedback Request - Gift Survey',
        body: `
Dear Recipient,

We hope you enjoyed your recent gift! We would greatly appreciate your feedback to help us improve our gift selection and delivery process.

Please take a few minutes to complete this brief survey:
${surveyUrl}

Thank you for your time and feedback.

Best regards,
The Spherical Team
        `
      };

      toast({
        title: "Survey Sent",
        description: `Survey email sent to ${recipientEmail}`,
      });

    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send survey email. Please try again.",
        variant: "destructive",
      });
    }
  };

  const addQuestion = () => {
    setQuestions([...questions, '']);
  };

  const updateQuestion = (index: number, value: string) => {
    const newQuestions = [...questions];
    newQuestions[index] = value;
    setQuestions(newQuestions);
  };

  const removeQuestion = (index: number) => {
    if (questions.length > 1) {
      setQuestions(questions.filter((_, i) => i !== index));
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ExternalLink className="h-5 w-5" />
          Google Forms Survey Integration
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {!surveyUrl ? (
          <>
            <div>
              <Label htmlFor="survey-title">Survey Title</Label>
              <Input
                id="survey-title"
                value={surveyTitle}
                onChange={(e) => setSurveyTitle(e.target.value)}
                placeholder="Enter survey title"
              />
            </div>

            <div>
              <Label htmlFor="survey-description">Survey Description</Label>
              <Textarea
                id="survey-description"
                value={surveyDescription}
                onChange={(e) => setSurveyDescription(e.target.value)}
                placeholder="Enter survey description"
                rows={3}
              />
            </div>

            <div>
              <Label>Survey Questions</Label>
              <div className="space-y-2 mt-2">
                {questions.map((question, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={question}
                      onChange={(e) => updateQuestion(index, e.target.value)}
                      placeholder={`Question ${index + 1}`}
                    />
                    {questions.length > 1 && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => removeQuestion(index)}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addQuestion}
                >
                  Add Question
                </Button>
              </div>
            </div>

            <Button 
              onClick={createGoogleForm} 
              disabled={isCreating || !surveyTitle.trim()}
              className="w-full"
            >
              {isCreating ? 'Creating Survey...' : 'Create Google Forms Survey'}
            </Button>
          </>
        ) : (
          <div className="space-y-4">
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <h4 className="font-medium text-green-800 mb-2">Survey Created Successfully!</h4>
              <p className="text-sm text-green-700 mb-3">
                Your Google Forms survey is ready. You can edit it directly in Google Forms or send it to recipients.
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(surveyUrl, '_blank')}
                  className="flex items-center gap-2"
                >
                  <ExternalLink className="h-4 w-4" />
                  Open in Google Forms
                </Button>
                {recipientEmail && (
                  <Button
                    size="sm"
                    onClick={sendSurveyEmail}
                    className="flex items-center gap-2"
                  >
                    <Send className="h-4 w-4" />
                    Send to Recipient
                  </Button>
                )}
              </div>
            </div>

            <div className="text-sm text-muted-foreground">
              <p><strong>Survey URL:</strong></p>
              <code className="block p-2 bg-gray-100 rounded text-xs break-all">
                {surveyUrl}
              </code>
            </div>
          </div>
        )}

        {recipientEmail && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-700">
              <strong>Recipient:</strong> {recipientEmail}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}